import React, { useState, useEffect } from 'react';
import { ArrowUpRight } from 'lucide-react';

export default function ItApproch() {
  const [isVisible, setIsVisible] = useState(false);
  const [expandedStep, setExpandedStep] = useState(0);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const steps = [
    {
      number: '01',
      title: 'Assess Needs',
      description: 'We analyze your business requirements, challenges, and goals to build a strong foundation for a tailored IT strategy.'
    },
    {
      number: '02',
      title: 'Design Solutions',
      description: 'We craft scalable, secure, and future-ready IT solutions aligned with your long-term business objectives and digital transformation goals.'
    },
    {
      number: '03',
      title: 'Implement Efficiently',
      description: 'Our team executes solutions with precision, ensuring minimal disruption, maximum performance, and seamless deployment.'
    },
    {
      number: '04',
      title: 'Monitor & Support',
      description: 'We continuously monitor systems, provide proactive support, and optimize performance to ensure long-term success..'
    }
  ];

  return (
    <div className=" bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-6">
      <div className="container mx-auto max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-6 md:space-y-8">
            {/* Badge */}
            <div className='flex justify-center sm:justify-start'>
            <div 
              className={`inline-flex items-center gap-2 px-4 py-2 border-2 border-slate-900 rounded-full bg-white transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
              }`}
            >
              <div className="w-2 h-2 bg-slate-900 rounded-sm"></div>
              <span className="text-slate-900 font-medium text-sm">Our IT Approach</span>
            </div>
            </div>

            {/* Heading */}
            <div className="space-y-2 text-center md:text-start">
              <h2 
                className={`text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight transition-all duration-1000 delay-200 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
              >
                Where Engineering Meets
              </h2>
              <h3 
                className={`text-3xl sm:text-4xl lg:text-5xl font-bold italic text-slate-900 leading-tight transition-all duration-1000 delay-300 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
              >
                Technology
              </h3>
            </div>

            {/* Description */}
            <p 
              className={`text-slate-600 text-justify text-base sm:text-lg leading-relaxed max-w-xl transition-all duration-1000 delay-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              Most software fails because it ignores how real operations work. We take an engineering-first approach — understanding workflows, constraints, and systems before building solutions.
            </p>

            {/* Button */}
            <div 
              className={`pt-4 transition-all duration-1000 delay-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <button className="group px-8 py-4 bg-slate-900 text-white rounded-xl font-medium hover:bg-slate-800 transition-all duration-300 hover:scale-105 hover:shadow-2xl flex items-center gap-3">
                Start Your Journey
                <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </button>
            </div>
          </div>

          {/* Right Content - Timeline */}
          <div className="relative">
            {/* Vertical Line */}
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-slate-300 -translate-x-1/2"></div>

            {/* Center Circle */}
            {/* <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
              <div 
                className={`w-64 h-64 border-2 border-slate-300 rounded-full flex items-center justify-center bg-white transition-all duration-1000 delay-500 ${
                  isVisible ? 'opacity-100 scale-100 rotate-0' : 'opacity-0 scale-0 rotate-180'
                }`}
              >
                <div className="text-center">
                  <div className="text-7xl font-bold text-slate-900">04</div>
                  <div className="text-xs font-semibold text-slate-600 tracking-widest mt-2">STEPS PROCESS</div>
                </div>
              </div>
            </div> */}

            {/* Steps */}
            <div className="relative space-y-4">
              {steps.map((step, index) => {
                const delay = 800 + index * 150;
                const isExpanded = expandedStep === parseInt(step.number);

                return (
                  <div
                    key={step.number}
                    className={`transition-all duration-700 ${
                      isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
                    }`}
                    style={{ transitionDelay: `${delay}ms` }}
                  >
                    <div 
                      className={`relative bg-slate-900 rounded-2xl p-6 cursor-pointer transition-all duration-300 ${
                        isExpanded 
                          ? 'shadow-2xl scale-105   ring-offset-4' 
                          : 'hover:shadow-xl hover:scale-102'
                      }`}
                      onClick={() => setExpandedStep(parseInt(step.number))}
                    >
                      {/* Step Header */}
                      <div className="flex items-center justify-between">
                        <div className={`px-4 py-2 rounded-lg font-bold transition-all duration-300 ${
                          isExpanded 
                            ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white' 
                            : 'bg-slate-800 text-slate-400'
                        }`}>
                          {step.number}
                        </div>
                        <h4 className="text-lg sm:text-xl font-bold text-white flex-1 text-right">{step.title}</h4>
                      </div>

                      {/* Step Description - Only show for expanded */}
                      <div 
                        className={`overflow-hidden transition-all duration-300 ${
                          isExpanded ? 'max-h-40 opacity-100 mt-4' : 'max-h-0 opacity-0'
                        }`}
                      >
                        <div className="pt-2 border-t border-slate-700">
                          <p className="text-slate-300 text-xs sm:text-sm leading-relaxed">
                            {step.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .hover\:scale-102:hover {
          transform: scale(1.02);
        }
      `}</style>
    </div>
  );
}